import React, { useState, useEffect, useCallback } from "react";
import { TabsContent } from "../ui/tabs";
import { RefreshCw, Brain, ThumbsUp, ThumbsDown, RotateCcw, Activity, TrendingUp, Flame } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../../config";
import { useAuth } from "../../AuthContext";

const Stat = ({ dm, label, value, accent, testid }) => (
  <div data-testid={testid} className={`rounded-lg p-3 ${dm ? "bg-[#252e2f]" : "bg-white"}`} style={{ boxShadow: "0 1px 3px rgba(0,0,0,0.08)" }}>
    <div className={`text-[10px] uppercase tracking-wider ${dm ? "text-slate-500" : "text-[#8a999c]"}`}>{label}</div>
    <div className={`text-lg font-bold mt-0.5 ${accent || (dm ? "text-slate-200" : "text-[#1f2a2c]")}`}>{value}</div>
  </div>
);

const WeightRow = ({ dm, term, w }) => (
  <div className="flex items-center justify-between text-xs py-1">
    <span className={`truncate mr-2 ${dm ? "text-slate-300" : "text-[#3c4a4d]"}`}>{term}</span>
    <span className={`font-mono font-semibold ${w >= 0 ? "text-emerald-500" : "text-red-500"}`}>
      {w >= 0 ? "+" : ""}{w.toFixed(3)}
    </span>
  </div>
);

/** Global feed settings — moved here from user Settings (redesign item 5).
 *  These are GLOBAL: they affect every user's feed. */
const GlobalFeedSettings = ({ dm }) => {
  const { authHeader } = useAuth();
  const [settings, setSettings] = useState(null);
  const [estimate, setEstimate] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/user-settings`, { headers: authHeader() })
      .then(r => setSettings(r.data.settings))
      .catch(() => toast.error("Could not load global feed settings"));
    // Static estimate, computed once on open (no live API)
    axios.get(`${API}/news`, { params: { page: 1, limit: 1 }, headers: authHeader() })
      .then(r => {
        const total = r.data.totalDbCount || 0;
        const visible = r.data.totalCount || 0;
        if (total > 0) setEstimate({ hidden: Math.max(0, total - visible), total });
      })
      .catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const saveGlobals = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/user-settings`, settings, { headers: authHeader() });
      toast.success("Global feed settings saved — they apply to every user");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  if (!settings) return null;
  const inputCls = `w-full rounded-lg px-3 py-2 text-sm border-0 outline-none ${dm ? "bg-[#252e2f] text-slate-200" : "bg-white text-[#1f2a2c]"}`;
  const labelCls = `block text-[10px] uppercase tracking-wider mb-1.5 ${dm ? "text-slate-500" : "text-[#8a999c]"}`;

  return (
    <div data-testid="global-feed-settings" className={`rounded-xl p-4 ${dm ? "bg-[#1a2122]" : "bg-[#f3f4f5]"}`}>
      <p className={`text-xs font-bold mb-1 ${dm ? "text-amber-300" : "text-[#8a5a06]"}`}>
        ⚠ Global feed settings — apply to EVERY user&apos;s feed
      </p>
      <p className={`text-[11px] mb-4 ${dm ? "text-slate-500" : "text-[#8a999c]"}`}>
        Moved out of user Settings: nothing here is personal.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div>
          <label className={labelCls}>Global ranking threshold (0.1–1.0)</label>
          <input type="number" step="0.1" min="0.1" max="1.0" data-testid="global-min-relevance-input"
            value={settings.minRelevanceScore ?? ""}
            onChange={e => setSettings({ ...settings, minRelevanceScore: parseFloat(e.target.value) })}
            className={inputCls} />
          <p className={`text-[10.5px] mt-1.5 ${dm ? "text-slate-500" : "text-[#8a999c]"}`} data-testid="global-threshold-estimate">
            {estimate
              ? `Currently hides ~${estimate.hidden.toLocaleString("en-US")} of ${estimate.total.toLocaleString("en-US")} stories (estimate — includes all global filters).`
              : "Applies to every user's feed."}
          </p>
        </div>
        <div>
          <label className={labelCls}>Max articles / source</label>
          <input type="number" min="5" max="100" data-testid="global-max-articles-input"
            value={settings.maxArticlesPerSource ?? ""}
            onChange={e => setSettings({ ...settings, maxArticlesPerSource: parseInt(e.target.value) })}
            className={inputCls} />
        </div>
        <div>
          <label className={labelCls}>Days to look back</label>
          <input type="number" min="1" max="30" data-testid="global-days-lookback-input"
            value={settings.daysToLookBack ?? ""}
            onChange={e => setSettings({ ...settings, daysToLookBack: parseInt(e.target.value) })}
            className={inputCls} />
        </div>
      </div>
      <div className="flex justify-end mt-4">
        <button data-testid="global-feed-settings-save-btn" onClick={saveGlobals} disabled={saving}
          className="text-xs px-4 py-2 rounded-lg bg-[#006874] text-white hover:bg-[#00525c] disabled:opacity-50">
          {saving ? "Saving…" : "Save global settings"}
        </button>
      </div>
    </div>
  );
};

export default function RankingHealthTab({ dm }) {
  const { authHeader } = useAuth();
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/ranking/learning/health`, { headers: authHeader() });
      setHealth(data);
    } catch {
      toast.error("Could not load ranking health");
    } finally {
      setLoading(false);
    }
  }, [authHeader]);

  useEffect(() => { load(); }, [load]);

  const rebuild = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/admin/ranking/learning/rebuild`, {}, { headers: authHeader() });
      toast.success(`Model rebuilt — ${data.labels_used} effective labels`);
      await load();
    } catch { toast.error("Rebuild failed"); } finally { setBusy(false); }
  };

  const reset = async () => {
    if (!window.confirm("Reset learning? Weights and centroids are wiped (labels are kept, audited action).")) return;
    setBusy(true);
    try {
      await axios.post(`${API}/admin/ranking/learning/reset`, {}, { headers: authHeader() });
      toast.success("Learning reset — F is neutral until the next rebuild");
      await load();
    } catch { toast.error("Reset failed"); } finally { setBusy(false); }
  };

  const h = health;
  const conv = h?.convergence?.status;
  const convColor = conv === "stable" ? "text-emerald-500" : conv === "drifting" ? "text-amber-500" : (dm ? "text-slate-500" : "text-[#8a999c]");

  const rec = h?.recompute;
  const recState = !h ? null
    : !h.ranking_enabled ? "disabled"
    : rec?.status === "error" ? "error"
    : rec?.stale ? "stale"
    : rec?.status === "ok" ? "ok" : "pending";
  const recColors = { ok: "text-emerald-500", error: "text-red-500", stale: "text-amber-500", pending: "text-amber-500", disabled: dm ? "text-slate-500" : "text-[#8a999c]" };
  const recLabel = { ok: "OK", error: "ERROR", stale: "STALE", pending: "pending…", disabled: "DISABLED" };

  return (
    <TabsContent value="ranking" className="space-y-4">
      <GlobalFeedSettings dm={dm} />

      <div className="flex items-center justify-between flex-wrap gap-2">
        <h3 className={`text-sm font-bold flex items-center gap-2 ${dm ? "text-slate-200" : "text-[#1f2a2c]"}`}>
          <Brain className="w-4 h-4" /> Ranking Health — Layer B learning
        </h3>
        <div className="flex items-center gap-2">
          <button data-testid="ranking-health-refresh-btn" onClick={load} disabled={loading}
            className={`text-xs px-3 py-1.5 rounded-lg flex items-center gap-1.5 ${dm ? "bg-[#252e2f] text-slate-300 hover:bg-[#2e3a3b]" : "bg-white text-[#3c4a4d] hover:bg-slate-50"}`}>
            <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} /> Refresh
          </button>
          <button data-testid="learning-rebuild-btn" onClick={rebuild} disabled={busy}
            className="text-xs px-3 py-1.5 rounded-lg flex items-center gap-1.5 bg-[#006874] text-white hover:bg-[#00525c] disabled:opacity-50">
            <Activity className="w-3 h-3" /> Rebuild now
          </button>
          <button data-testid="learning-reset-btn" onClick={reset} disabled={busy}
            className="text-xs px-3 py-1.5 rounded-lg flex items-center gap-1.5 bg-red-600/90 text-white hover:bg-red-700 disabled:opacity-50">
            <RotateCcw className="w-3 h-3" /> Reset learning
          </button>
        </div>
      </div>

      {!h ? (
        <div className={`text-sm py-8 text-center ${dm ? "text-slate-500" : "text-[#8a999c]"}`}>{loading ? "Loading…" : "No data"}</div>
      ) : (
        <>
          {/* Status strip */}
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2">
            <Stat dm={dm} testid="rh-ranking-flag" label="Ranking flag" value={h.ranking_enabled ? "ON" : "OFF"} accent={h.ranking_enabled ? "text-emerald-500" : "text-red-500"} />
            <Stat dm={dm} testid="rh-learning-flag" label="Learning" value={h.learning.enabled ? (h.learning.active ? "ACTIVE" : "warming up") : "OFF"} accent={h.learning.active ? "text-emerald-500" : "text-amber-500"} />
            <Stat dm={dm} testid="rh-labels" label={`Labels (min ${h.learning.min_labels})`} value={`${h.learning.labels_used}${h.learning.min_labels_reached ? " ✓" : ""}`} />
            <Stat dm={dm} testid="rh-wlearn" label="w_learn effective" value={`${h.learning.w_learn_effective} / ${h.learning.w_learn}`} />
            <Stat dm={dm} testid="rh-influenced" label="Articles influenced" value={`${h.f_stats.influenced} / ${h.f_stats.scored_docs}`} />
            <Stat dm={dm} testid="rh-recompute-state" label="Recompute" value={recLabel[recState] || "n/a"} accent={recColors[recState]} />
            <Stat dm={dm} testid="rh-convergence" label="Convergence" value={conv || "n/a"} accent={convColor} />
          </div>

          {/* Recompute batch health (iter102) */}
          <div data-testid="rh-recompute-card" className={`rounded-xl p-4 ${dm ? "bg-[#1f2728]" : "bg-white"}`} style={{ boxShadow: "0 1px 3px rgba(0,0,0,0.08)" }}>
            <div className={`text-xs font-bold mb-2 flex items-center gap-1.5 ${dm ? "text-slate-300" : "text-[#3c4a4d]"}`}>
              <Activity className={`w-3.5 h-3.5 ${recColors[recState]}`} /> Recompute batch — last pass
              <span data-testid="rh-recompute-badge"
                className={`ml-1 px-1.5 py-0.5 rounded text-[10px] font-bold ${
                  recState === "ok" ? "bg-emerald-500/15 text-emerald-500"
                  : recState === "error" ? "bg-red-500/15 text-red-500"
                  : recState === "stale" || recState === "pending" ? "bg-amber-500/15 text-amber-500"
                  : dm ? "bg-white/10 text-slate-400" : "bg-black/5 text-[#8a999c]"}`}>
                {recState === "disabled" ? "ranking OFF" : recLabel[recState]}
              </span>
            </div>
            {!rec?.last_run_at ? (
              <div className={`text-xs ${dm ? "text-slate-500" : "text-[#8a999c]"}`}>No pass recorded yet</div>
            ) : (
              <div className={`text-xs space-y-1 ${dm ? "text-slate-400" : "text-[#6b7a7d]"}`}>
                <div data-testid="rh-recompute-lastrun">
                  Last pass: <b>{new Date(rec.last_run_at).toLocaleString()}</b> · status{" "}
                  <b className={rec.status === "error" ? "text-red-500" : rec.status === "ok" ? "text-emerald-500" : recColors.disabled}>{rec.status}</b>
                  {rec.duration_ms != null && <> · {rec.duration_ms} ms</>}
                  {rec.scored_count != null && <> · scored {rec.scored_count}</>}
                  {rec.influenced_count != null && <> · influenced {rec.influenced_count}</>}
                  {rec.retries > 0 && <span className="text-amber-500"> · {rec.retries} {rec.retries === 1 ? "retry" : "retries"}</span>}
                </div>
                {rec.status === "error" && rec.error_msg && (
                  <div className="text-red-500 font-mono break-all" data-testid="rh-recompute-error">{rec.error_msg}</div>
                )}
                {rec.stale && h.ranking_enabled && (
                  <div className="text-amber-500" data-testid="rh-recompute-stale">
                    ⚠ No green pass for &gt;{2 * (rec.interval_minutes || 15)} min — watchdog alert {rec.stale_alerted_at ? "sent" : "pending"}
                  </div>
                )}
                <div>Last green: {rec.last_ok_at ? new Date(rec.last_ok_at).toLocaleString() : "never"} · interval {rec.interval_minutes} min</div>
              </div>
            )}
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            {/* Labels collected */}
            <div className={`rounded-xl p-4 ${dm ? "bg-[#1f2728]" : "bg-white"}`} style={{ boxShadow: "0 1px 3px rgba(0,0,0,0.08)" }}>
              <div className={`text-xs font-bold mb-2 flex items-center gap-1.5 ${dm ? "text-slate-300" : "text-[#3c4a4d]"}`}>
                <ThumbsUp className="w-3.5 h-3.5 text-emerald-500" /> Labels collected
              </div>
              <div className="flex items-center gap-4 text-sm" data-testid="rh-label-counts">
                <span className="text-emerald-500 font-semibold">▲ {h.labels.promote} promote</span>
                <span className="text-red-500 font-semibold">▼ {h.labels.demote} demote</span>
                <span className={dm ? "text-slate-500" : "text-[#8a999c]"}>{h.labels.clear} clear</span>
              </div>
              <div className={`text-[11px] mt-1 ${dm ? "text-slate-500" : "text-[#8a999c]"}`}>
                Last label: {h.labels.last_label_at ? new Date(h.labels.last_label_at).toLocaleString() : "never"}
              </div>
              {/* 30d mini bars */}
              {h.labels.daily_30d?.length > 0 && (
                <div className="flex items-end gap-[2px] h-12 mt-3" data-testid="rh-daily-bars">
                  {h.labels.daily_30d.map((d) => {
                    const tot = d.promote + d.demote;
                    return (
                      <div key={d.day} title={`${d.day}: +${d.promote} / -${d.demote}`} className="flex flex-col justify-end flex-1 max-w-[14px] h-full">
                        <div className="bg-emerald-500/80 rounded-sm" style={{ height: `${Math.min(100, d.promote * 12)}%` }} />
                        <div className="bg-red-500/80 rounded-sm mt-[1px]" style={{ height: `${Math.min(100, d.demote * 12)}%` }} />
                        {tot === 0 && <div className={`h-[2px] ${dm ? "bg-white/10" : "bg-black/10"}`} />}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Active overrides */}
            <div className={`rounded-xl p-4 ${dm ? "bg-[#1f2728]" : "bg-white"}`} style={{ boxShadow: "0 1px 3px rgba(0,0,0,0.08)" }}>
              <div className={`text-xs font-bold mb-2 flex items-center gap-1.5 ${dm ? "text-slate-300" : "text-[#3c4a4d]"}`}>
                <Flame className="w-3.5 h-3.5 text-orange-500" /> Active overrides (Layer A) — {h.overrides.active_n}
              </div>
              {h.overrides.items.length === 0 ? (
                <div className={`text-xs ${dm ? "text-slate-500" : "text-[#8a999c]"}`}>None — no per-item boosts in effect</div>
              ) : (
                <div className="space-y-1.5 max-h-40 overflow-y-auto" data-testid="rh-overrides-list">
                  {h.overrides.items.map((o) => (
                    <div key={o.target_id} className="flex items-center justify-between text-xs gap-2">
                      <span className={`truncate ${dm ? "text-slate-300" : "text-[#3c4a4d]"}`}>
                        {o.action === "promote" ? "👍" : "👎"} {o.title || o.target_id}
                      </span>
                      <span className={`whitespace-nowrap ${dm ? "text-slate-500" : "text-[#8a999c]"}`}>
                        exp {new Date(o.expires_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Learned weights */}
            <div className={`rounded-xl p-4 ${dm ? "bg-[#1f2728]" : "bg-white"}`} style={{ boxShadow: "0 1px 3px rgba(0,0,0,0.08)" }}>
              <div className={`text-xs font-bold mb-2 flex items-center gap-1.5 ${dm ? "text-slate-300" : "text-[#3c4a4d]"}`}>
                <TrendingUp className="w-3.5 h-3.5 text-[#006874]" /> Learned weights (B1)
              </div>
              {!h.model ? (
                <div className={`text-xs ${dm ? "text-slate-500" : "text-[#8a999c]"}`}>No model yet — labels accumulate, rebuild runs hourly</div>
              ) : (
                <div data-testid="rh-weights">
                  {h.model.topic_weights_top.length === 0 && <div className={`text-xs ${dm ? "text-slate-500" : "text-[#8a999c]"}`}>No topic weights above noise floor</div>}
                  {h.model.topic_weights_top.map(([t, w]) => <WeightRow key={t} dm={dm} term={t} w={w} />)}
                  {h.model.source_weights_top.length > 0 && (
                    <div className={`text-[10px] uppercase tracking-wider mt-2 mb-1 ${dm ? "text-slate-500" : "text-[#8a999c]"}`}>Sources</div>
                  )}
                  {h.model.source_weights_top.map(([s, w]) => <WeightRow key={s} dm={dm} term={s} w={w} />)}
                </div>
              )}
            </div>

            {/* Centroids + F distribution */}
            <div className={`rounded-xl p-4 ${dm ? "bg-[#1f2728]" : "bg-white"}`} style={{ boxShadow: "0 1px 3px rgba(0,0,0,0.08)" }}>
              <div className={`text-xs font-bold mb-2 flex items-center gap-1.5 ${dm ? "text-slate-300" : "text-[#3c4a4d]"}`}>
                <ThumbsDown className="w-3.5 h-3.5 text-red-500" /> Centroids (B2) &amp; F distribution
              </div>
              <div className="text-xs space-y-1" data-testid="rh-centroids">
                <div className={dm ? "text-slate-300" : "text-[#3c4a4d]"}>
                  Promo centroid: <b>{h.model?.has_promo_centroid ? "✓" : "—"}</b> · Demo centroid: <b>{h.model?.has_demo_centroid ? "✓" : "—"}</b>
                </div>
                <div className={dm ? "text-slate-400" : "text-[#6b7a7d]"}>
                  Drift: promo {h.convergence.promo_drift ?? "n/a"} · demo {h.convergence.demo_drift ?? "n/a"} → <span className={convColor}>{conv}</span>
                </div>
                <div className={dm ? "text-slate-400" : "text-[#6b7a7d]"}>
                  F on scored articles: mean {h.f_stats.mean} · range [{h.f_stats.min}, {h.f_stats.max}]
                </div>
                <div className={dm ? "text-slate-400" : "text-[#6b7a7d]"}>
                  Autocal bounds: [{h.autocal.sim_norm_min ?? "—"}, {h.autocal.sim_norm_max ?? "—"}]
                  {h.autocal.computed_at ? ` · ${new Date(h.autocal.computed_at).toLocaleDateString()}` : ""}
                </div>
                <div className={`pt-1 ${dm ? "text-slate-500" : "text-[#8a999c]"}`}>
                  Model built: {h.model?.computed_at ? new Date(h.model.computed_at).toLocaleString() : "never"} ·
                  α {h.learning.alpha} / β {h.learning.beta} · gate crosses floor: {h.learning.can_cross_relevance_floor ? "yes" : "no"}
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </TabsContent>
  );
}
